import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useLocale } from '@/contexts/LocaleContext'
import { rememberPendingInvite } from '@/lib/pendingInvite'
import { getTeamErrorCode, teamService, type InvitationPreview } from '@/services/teamService'
import AuthShell from '@/components/AuthShell'

type Phase = 'loading' | 'ready' | 'joining' | 'joined' | 'dead'

/**
 * Landing page for an invitation link.
 *
 * Public on purpose: the invitee may not have an account yet, so the page has
 * to name the team before asking anybody to sign in. The token is parked in
 * session storage so the login/registration round trip comes back here.
 */
export default function TeamJoinPage() {
  const { t } = useTranslation('team')
  const { language } = useLocale()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const [phase, setPhase] = useState<Phase>('loading')
  const [preview, setPreview] = useState<InvitationPreview | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    if (!token) {
      setPhase('dead')
      setError(t('join.errors.invalid_invitation'))
      return
    }
    rememberPendingInvite(token)
    teamService
      .previewInvitation(token)
      .then((data) => {
        if (cancelled) return
        setPreview(data)
        setPhase('ready')
      })
      .catch(() => {
        if (cancelled) return
        setPhase('dead')
        setError(t('join.errors.invalid_invitation'))
      })
    return () => {
      cancelled = true
    }
  }, [token, t])

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(language, { day: 'numeric', month: 'long', year: 'numeric' }),
    [language],
  )

  const handleJoin = useCallback(async () => {
    setPhase('joining')
    setError('')
    try {
      await teamService.acceptInvitation(token)
      setPhase('joined')
    } catch (err) {
      const code = getTeamErrorCode(err)
      setError(t(`join.errors.${code}`, { defaultValue: t('join.errors.generic') }))
      setPhase('ready')
    }
  }, [token, t])

  if (phase === 'loading') {
    return (
      <AuthShell title={t('join.loading')} icon={<GroupsOutlinedIcon />}>
        <Stack alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={28} />
        </Stack>
      </AuthShell>
    )
  }

  if (phase === 'dead') {
    return (
      <AuthShell title={t('join.title', { team: '' }).trim()} subtitle={error} icon={<GroupsOutlinedIcon />} iconTone="error">
        <Button fullWidth variant="contained" size="large" onClick={() => navigate('/login')} sx={{ py: 1.25 }}>
          {t('join.login')}
        </Button>
      </AuthShell>
    )
  }

  if (phase === 'joined') {
    return (
      <AuthShell
        title={t('join.success', { team: preview?.team_name ?? '' })}
        icon={<CheckCircleOutlineIcon />}
        iconTone="success"
      >
        <Button fullWidth variant="contained" size="large" onClick={() => navigate('/team')} sx={{ py: 1.25 }}>
          {t('join.goToTeam')}
        </Button>
      </AuthShell>
    )
  }

  const subtitle = [
    preview?.organization,
    preview?.invited_by ? t('join.invitedBy', { who: preview.invited_by }) : '',
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <AuthShell
      title={t('join.title', { team: preview?.team_name ?? '' })}
      subtitle={subtitle}
      icon={<GroupsOutlinedIcon />}
    >
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}

        {isAuthenticated ? (
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleJoin}
            disabled={phase === 'joining'}
            sx={{ py: 1.25 }}
          >
            {phase === 'joining' ? t('join.joining') : t('join.accept')}
          </Button>
        ) : (
          <>
            <Typography sx={{ fontSize: 14, color: 'text.secondary', textAlign: 'center' }}>
              {preview?.has_account
                ? t('join.loginFirst', { email: preview.email })
                : t('join.registerFirst', { email: preview?.email ?? '' })}
            </Typography>
            <Button fullWidth variant="contained" size="large" onClick={() => navigate('/login')} sx={{ py: 1.25 }}>
              {t('join.login')}
            </Button>
            <Button fullWidth variant="outlined" size="large" onClick={() => navigate('/register')} sx={{ py: 1.25 }}>
              {t('join.register')}
            </Button>
          </>
        )}

        {preview?.expires_at && (
          <Typography sx={{ fontSize: 12.5, color: 'text.disabled', textAlign: 'center' }}>
            {t('join.expires', { date: dateFmt.format(new Date(preview.expires_at)) })}
          </Typography>
        )}
      </Stack>
    </AuthShell>
  )
}
